/**
 * Button Component
 *
 * Reusable button with solid and outlined variants.
 * Styles controlled via CSS custom properties for easy theming.
 *
 * @param {object} props
 * @param {string} props.variant - "solid", "outlined", or compound e.g. "solid green"
 * @param {string} props.href - If provided, renders as anchor tag
 * @param {string} props.target - Link target (_blank, etc.)
 * @param {function} props.onClick - Click handler
 * @param {string} props.type - Button type (button, submit)
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional class names
 * @param {React.ReactNode} props.children - Button content
 */

import React from "react";
import { Link } from "gatsby";
import * as styles from "./Button.module.scss";

const Button = ({
  variant = "solid",
  href,
  target,
  onClick,
  type = "button",
  disabled = false,
  className = "",
  children,
  ...props
}) => {
  const variantClasses = (variant || "solid")
    .split(/\s+/)
    .map(v => styles[v])
    .filter(Boolean);
  const classNames = [
    styles.button,
    ...variantClasses,
    disabled && styles.disabled,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // External link
  if (href && (href.startsWith("http") || href.startsWith("mailto:"))) {
    return (
      <a
        href={href}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        className={classNames}
        onClick={onClick}
        {...props}
      >
        {children}
      </a>
    );
  }

  // Internal link
  if (href) {
    return (
      <Link to={href} className={classNames} onClick={onClick} {...props}>
        {children}
      </Link>
    );
  }

  // Button
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classNames}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
